import React from 'react';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Divider,
  Stack,
  SvgIcon,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import SunIcon from '@heroicons/react/24/outline/SunIcon';
import MoonIcon from '@heroicons/react/24/outline/MoonIcon';
import ComputerDesktopIcon from '@heroicons/react/24/outline/ComputerDesktopIcon';
import ArrowRightStartOnRectangleIcon from '@heroicons/react/24/outline/ArrowRightStartOnRectangleIcon';
import { MobileBottomSheet } from '../../components/mobile/mobile-bottom-sheet';
import { isPathActive } from '../../router/navigation';

function SectionTile({ label, icon, selected, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        flexDirection: 'column',
        gap: 0.75,
        p: 1.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'action.selected' : 'background.paper',
        color: selected ? 'primary.main' : 'text.primary',
        minHeight: 84,
        textAlign: 'center',
      }}
    >
      {icon && (
        <Box sx={{ display: 'flex', color: selected ? 'primary.main' : 'text.secondary' }}>
          {icon}
        </Box>
      )}
      <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
        {label}
      </Typography>
    </ButtonBase>
  );
}

export default function MobileMoreSheet({ open, onClose, sections, pathname, user, onNavigate, onSignOut }) {
  const { mode, setMode } = useColorScheme();

  return (
    <MobileBottomSheet
      open={open}
      onClose={onClose}
      title="Menu"
      footer={
        <Button
          fullWidth
          size="large"
          variant="outlined"
          color="error"
          startIcon={
            <SvgIcon fontSize="small">
              <ArrowRightStartOnRectangleIcon />
            </SvgIcon>
          }
          onClick={onSignOut}
        >
          Logout
        </Button>
      }
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Avatar sx={{ width: 44, height: 44, bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 700 }}>
          {user?.name?.trim()?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700 }}>
            {user?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="p">
            {user?.role?.replace(/_/g, ' ')}
          </Typography>
        </Box>
      </Stack>

      {sections.map((section) => {
        const pages = section.children.length > 0
          ? section.children
          : [{ title: section.title, path: section.path }];

        return (
          <Box key={section.key} sx={{ mb: 2 }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
              {section.title}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mt: 0.5 }}>
              {pages.map((page) => (
                <SectionTile
                  key={page.path}
                  label={page.title}
                  icon={section.icon}
                  selected={isPathActive(pathname, page.path)}
                  onClick={() => onNavigate(page.path)}
                />
              ))}
            </Box>
          </Box>
        );
      })}

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
        Appearance
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={mode || 'system'}
        onChange={(event, value) => value && setMode(value)}
        sx={{ mt: 0.5, mb: 1 }}
      >
        <ToggleButton value="light">
          <SvgIcon fontSize="small" sx={{ mr: 0.75 }}><SunIcon /></SvgIcon>
          Light
        </ToggleButton>
        <ToggleButton value="dark">
          <SvgIcon fontSize="small" sx={{ mr: 0.75 }}><MoonIcon /></SvgIcon>
          Dark
        </ToggleButton>
        <ToggleButton value="system">
          <SvgIcon fontSize="small" sx={{ mr: 0.75 }}><ComputerDesktopIcon /></SvgIcon>
          System
        </ToggleButton>
      </ToggleButtonGroup>
    </MobileBottomSheet>
  );
}
